<script setup lang="tsx">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { RiskMeta } from '@/models/api-generated'
import VulnerabilitiesCard from './VulnerabilitiesCard.vue'
import ThreatsCard from './ThreatsCard.vue'
import Modal from '@/components/shared/Modal.vue'

const props = defineProps<{ isEdit: boolean }>()

const { t } = useI18n()
const model = defineModel<RiskMeta>({ required: true })

const showConnectionModal = ref(false)
const threats = ref(model.value.threats)
const vulnerabilities = ref(model.value.vulnerabilities)

const reset = () => {
	threats.value = []
	vulnerabilities.value = []
	showConnectionModal.value = false
}

const onConfirm = () => {
	model.value.threats = threats.value
	model.value.vulnerabilities = vulnerabilities.value
	showConnectionModal.value = false
}
</script>

<template>
	<div class="flex-grow-1 text-nowrap">
		<div class="mb-2">
			<span class="fw-bold">{{ t('risk.objectConnection') }}</span>
		</div>

		<div v-one-tooltip="model.threats.map((t) => t.threat.name).join(', ')">{{ t('risk.threat', 2) }} ({{ model.threats.length }})</div>

		<div v-one-tooltip="model.vulnerabilities.map((t) => t.vulnerability.name).join(', ')">
			{{ t('risk.vulnerability', 2) }} ({{ model.vulnerabilities.length }})
		</div>
		<template v-if="props.isEdit">
			<button
				v-if="props.isEdit"
				type="button"
				class="btn btn-outline-primary btn-sm mt-1"
				:data-test="'edit-risk-connections-' + model.id"
				@click="showConnectionModal = true"
			>
				<i class="bi bi-plus-circle me-1"></i> {{ $t('risk.editConnection') }}
			</button>
		</template>
	</div>

	<Modal v-model="showConnectionModal" type="add-vulnerability" class="modal-xl">
		<template #header>
			<h5>{{ t('risk.editConnection') }}</h5>
		</template>

		<template #body>
			<div class="d-flex gap-2 flex-nowrap align-items-stretch w-100">
				<div class="flex-fill d-flex">
					<VulnerabilitiesCard v-model="vulnerabilities" :is-edit="true" :risk-meta-id="model.id" class="connection-card" />
				</div>

				<div class="flex-fill d-flex">
					<ThreatsCard v-model="threats" :is-edit="true" :risk-meta-id="model.id" class="connection-card" />
				</div>
			</div>
		</template>

		<template #footer>
			<button :data-test="'cancel-edit-connection'" type="button" class="btn btn-secondary" data-bs-dismiss="modal" @click="reset">
				{{ t('misc.cancel') }}
			</button>

			<button :data-test="'confirm-edit-connection'" type="button" class="btn btn-primary" @click="onConfirm">
				{{ t('misc.confirm') }}
			</button>
		</template>
	</Modal>
</template>
